/**
 * Execution Log Formatter Utility
 *
 * Formats execution logs from the device store into a format suitable
 * for sending to the AI chat backend. Designed to be token-efficient.
 */

import { ExecutionStep, ExecutionLog } from '@/stores/devices/device-store';

/**
 * Execution context summary
 */
export interface ExecutionSummary {
  totalSteps: number;
  completedSteps: number;
  errorSteps: number;
  skippedSteps: number;
  lastError?: string;
  lastErrorNodeId?: string;
  duration?: number;
}

/**
 * Execution context for AI chat
 */
export interface ExecutionContext {
  source: 'server' | 'esp32';
  executionKey: string;
  workflowId?: string;
  steps?: ExecutionStep[];
  logs?: ExecutionLog[];
  summary?: ExecutionSummary;
}

/**
 * Options for formatting execution context
 */
export interface FormatOptions {
  /** Maximum number of steps to include (default: 50) */
  maxSteps?: number;
  /** Maximum number of logs to include (default: 30) */
  maxLogs?: number;
  /** Include only errors and warnings in logs (default: false) */
  errorsOnly?: boolean;
}

const DEFAULT_OPTIONS: Required<FormatOptions> = {
  maxSteps: 50,
  maxLogs: 30,
  errorsOnly: false,
};

/**
 * Build execution context from device store data
 */
export function buildExecutionContext(
  source: 'server' | 'esp32',
  executionKey: string,
  steps: ExecutionStep[],
  logs: ExecutionLog[],
  workflowId?: string,
  options: FormatOptions = {}
): ExecutionContext {
  const opts = { ...DEFAULT_OPTIONS, ...options };

  // Filter and limit steps (most recent first)
  const filteredSteps = [...steps]
    .sort((a, b) => b.timestamp - a.timestamp)
    .slice(0, opts.maxSteps);

  // Filter and limit logs (most recent first)
  let filteredLogs = [...logs].sort((a, b) => b.timestamp - a.timestamp);
  if (opts.errorsOnly) {
    filteredLogs = filteredLogs.filter(l => l.level === 'error' || l.level === 'warn');
  }
  filteredLogs = filteredLogs.slice(0, opts.maxLogs);

  // Build summary
  const summary = buildSummary(steps);

  return {
    source,
    executionKey,
    workflowId,
    steps: filteredSteps,
    logs: filteredLogs,
    summary,
  };
}

/**
 * Build execution summary from steps
 */
function buildSummary(steps: ExecutionStep[]): ExecutionSummary {
  const summary: ExecutionSummary = {
    totalSteps: steps.length,
    completedSteps: 0,
    errorSteps: 0,
    skippedSteps: 0,
  };

  for (const step of steps) {
    switch (step.status) {
      case 'completed':
        summary.completedSteps++;
        break;
      case 'error':
        summary.errorSteps++;
        if (!summary.lastError) {
          summary.lastError = step.message;
          summary.lastErrorNodeId = step.nodeId;
        }
        break;
      case 'skipped':
        summary.skippedSteps++;
        break;
    }
  }

  // Calculate duration if we have first and last step
  if (steps.length >= 2) {
    const timestamps = steps.map(s => s.timestamp).sort((a, b) => a - b);
    summary.duration = timestamps[timestamps.length - 1] - timestamps[0];
  }

  return summary;
}

/**
 * Estimate token count for the execution context
 * Rough estimate: ~4 characters per token
 */
export function estimateTokenCount(context: ExecutionContext): number {
  // Rough estimation based on content
  let charCount = 0;

  // Summary
  charCount += 200; // Fixed overhead for summary section

  // Steps
  if (context.steps) {
    charCount += context.steps.length * 80; // ~80 chars per step
  }

  // Logs
  if (context.logs) {
    charCount += context.logs.length * 100; // ~100 chars per log
  }

  return Math.ceil(charCount / 4);
}

/**
 * Check if context has meaningful data worth sending
 */
export function hasExecutionData(context: ExecutionContext): boolean {
  return (
    (context.steps && context.steps.length > 0) ||
    (context.logs && context.logs.length > 0)
  );
}

/**
 * Format execution context for display in UI (preview)
 */
export function formatForPreview(context: ExecutionContext): string {
  const parts: string[] = [];

  if (context.summary) {
    parts.push(`${context.summary.totalSteps} steps`);
    if (context.summary.errorSteps > 0) {
      parts.push(`${context.summary.errorSteps} errors`);
    }
  }

  if (context.logs && context.logs.length > 0) {
    const errorCount = context.logs.filter(l => l.level === 'error').length;
    const warnCount = context.logs.filter(l => l.level === 'warn').length;
    if (errorCount > 0) parts.push(`${errorCount} error logs`);
    if (warnCount > 0) parts.push(`${warnCount} warnings`);
  }

  return parts.join(', ') || 'No execution data';
}
